import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface PictureFrameParams {
  frameStyle: number;
  frameWidth: number;
  frameColor: { r: number; g: number; b: number };
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const PictureFrame: Filter<PictureFrameParams> = {
  id: "picture-frame",
  displayName: "Picture Frame",
  defaultParams: { frameStyle: 1, frameWidth: 20, frameColor: { r: 139, g: 90, b: 43 } },

  apply(input: PixelBuffer, params: PictureFrameParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const frameW = Math.max(1, Math.round(params.frameWidth));
    const fc = params.frameColor;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const distLeft = x;
        const distRight = w - 1 - x;
        const distTop = y;
        const distBottom = h - 1 - y;
        const minDist = Math.min(distLeft, distRight, distTop, distBottom);

        if (minDist < frameW) {
          const t = minDist / frameW;
          let shade: number;

          switch (params.frameStyle) {
            case 1:
              shade = 0.5 + 0.5 * Math.sin(t * Math.PI);
              break;
            case 2:
              shade = t;
              break;
            case 3:
              shade = 1 - t * 0.3;
              break;
            default:
              shade = 1;
          }

          dst[o] = clamp8(fc.r * shade);
          dst[o + 1] = clamp8(fc.g * shade);
          dst[o + 2] = clamp8(fc.b * shade);
          dst[o + 3] = 255;
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
